import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '../../state-management/storage.service/storage.service';
import { GetArtBySearchOnExploreResponse } from '../../../models/interfaces/user-service';
import { LOCAL_STORAGE, ROLE } from '../../../models/constant/constants';
import { AddtoAlbumResponse, TotalArtStatsResponse } from '../../../models/interfaces/response';
import { RedirectManagerService } from '../../state-management/redirect.service/redirect-manager.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserArtistApiService {
  private baseUrl = environment.apiUrl;
  artistId: string | number;
  role:ROLE = ROLE.GUEST;

  cache = new Map<string, any>();
  constructor(
    private http: HttpClient,
    private toaster: ToastrService,
    private storageService: StorageService,
    private redirectService: RedirectManagerService,
    private router:Router,
  ) {
    this.artistId = this.storageService.getArtistId();
  }

  userUpdateRole(defaultRoles: ROLE = ROLE.AUDIENCE): Observable<any> {
    this.role = defaultRoles;
    this.storageService.syncRole(this.role);
    const url = `${this.baseUrl}/updateRoleStatus`;

    // Add URL parameters
    const params = new HttpParams().set('defaultRoles', defaultRoles);

    // Make the PUT request
    return this.http.put<any>(url, {}, { params });
  }

  // get Category
  getCategoryList() {
    const url = `${this.baseUrl}/getCategoryList`;

    if(this.cache.has(url)) {
      return of(this.cache.get(url))
    }
    return this.http.get(url).pipe(tap(data => this.cache.set(url, data)));
  }

  submitArtDetails(payload: any) {
    const url = `${this.baseUrl}/addArtDetails`;
    return this.http.post(url, payload);
  }


  getAllArtDetailByArtist(page: number, limit = 21): Observable<any> {
    const params = new HttpParams().set('limit', limit).set('page', page);
    const url = `${this.baseUrl}/getAllArtDetailByArtistId`;
    return this.http.get(url, { params });
  }

  getTotalArtStats(): Observable<TotalArtStatsResponse> {
    this.artistId = this.storageService.getArtistId();
    return this.http.get<TotalArtStatsResponse>(`${this.baseUrl}/getArtsStats?artistId=${this.artistId}`);
  }

  // api redirect
  googleRedirect() {
    const url = `https://accounts.google.com/o/oauth2/auth?client_id=826692707372-dnh4qbqe8qs9gkjqe70b0ikn2d79doh3.apps.googleusercontent.com&redirect_uri=http://localhost:6060/auth/google/callback&response_type=code&scope=https://www.googleapis.com/auth/photoslibrary.readonly&prompt=select_account`;
    return this.http.post(url, {});
  }

  // editArtDetails
  editArtDetails(artDetails: any, artDetailId: string): Observable<any> {
    const url = `${this.baseUrl}/editArtDetails/${artDetailId}`;
    return this.http.put(url, artDetails);
  }

  // DeleteArt
  deleteArt(deletedArtId: string): Observable<any> {
    const url = `${this.baseUrl}/deleteArtDetailByArtDetailId/${deletedArtId}`;
    return this.http.delete(url);
  }

  // google
  googlePhotosByArtistId(artOrAlbum:string | number ='ART') {
    // get /api/v1/googlePhotosByArtistId
    const url = `${this.baseUrl}/googlePhotosByArtistId?artOrAlbum=${artOrAlbum}`;
    return this.http.get(url);
  }

  // get google photos form the api
  // get /api/v1/getArtistGooglePhotos
  getArtistGooglePhotos(
    artistProfileId: string | null,
    accessToken: string | null,
    pageNo: number = 0,
    limit: number = 20
  ): Observable<any> {
    const params = new HttpParams()
      .set('artistId', artistProfileId || '')
      .set('accessToken', accessToken || '')
      .set('pageNo', pageNo.toString())
      .set('limit', limit.toString());

    const url = `${this.baseUrl}/getArtistGooglePhotos`;
    return this.http.get(url, { params });
  }

  // upload google iamge
  uploadGoodleImages(imageList: string[]) {
    // post /api/v1/uploadGooglePhotos
    const url = `${this.baseUrl}/uploadGooglePhotos`;
    return this.http.post(url, { googlePhotosList: imageList });
  }

  // file uplaod
  uploadFiles(files: File[] | null,isSignature = false): Observable<any> {
    const params = new HttpParams().set('isSignature', isSignature)
    const url = `${this.baseUrl}/fileUpload`;
    const formData = new FormData();

    // Append each file to the FormData
    if (files) {
      files.forEach((file, index) => {
        formData.append(`multipartFileList`, file);
      });
    } else {
    }

    return this.http
      .post<{ imgUrls: string[] }>(url, formData, {
        params,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(catchError((err) => this.handleError(err)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.toaster.error(error.message || error.error.message);
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Error: ${error.error.message}`;
    } else if (error.status) {
      // Backend returned an error
      errorMessage = `Error ${error.status}: ${error.message}`;
    }
    return throwError(() => errorMessage);
  }

  createSubscriptionPlan(
    artistId: string | number,
    planData: any
  ): Observable<any> {
    const url = `${this.baseUrl}/createSubscriptionPlan/${artistId}`;
    return this.http.post(url, planData);
  }

  getSubscriptionPlan(artistId: string | number): Observable<any> {
    const url = `${this.baseUrl}/getAllSubscriptionPlansForArtist/${artistId}`;
    return this.http.get(url);
  }

  deleteSubscriptionPlans(subscriptionId: string): Observable<any> {
    const url = `${this.baseUrl}/deleteSubscriptionPlansBySubId/${subscriptionId}`;
    return this.http.delete(url);
  }

  editSubscriptionPlan(
    subscriptionId: string,
    artistId: string | number,
    planDetails: any
  ): Observable<any> {
    const url = `${this.baseUrl}/updatedSubscriptionPlan/${subscriptionId}/${artistId}`;
    return this.http.put(url, planDetails);
  }

  // Albumn API
  //addArtistAlbum/{artistId}
  createArtistAlbum(AlbumData: any): Observable<any> {
    // Todo Update artist ID on Login
    this.artistId = this.storageService.getArtistId();

    const url = `${this.baseUrl}/addArtistAlbum/${this.artistId}`;
    return this.http.post(url, AlbumData);
  }

  // get /api/v1/getArtistAlbum
  // getArtistAlbum(): Observable<any> {
  //   const url = `${this.baseUrl}/getArtistAlbum`;
  //   return this.http.get(url);
  // }
  getArtistAlbum(page: number, limit: number, withLoader = true): Observable<any> {

    let headers = new HttpHeaders();
    if(!withLoader) {
      headers = headers.set("no-loader","yes");
    }

    const url = `${this.baseUrl}/getArtistAlbum?pageNo=${page}&limit=${limit}`;
    return this.http.get(url, { headers });
  }

  //addImagesInAlbum
  addImageToAlbum(albumData: any): Observable<AddtoAlbumResponse> {
    const { imageIds } = albumData;
    const url = `${this.baseUrl}/addImagesInAlbum/${albumData.albumId}`;
    return this.http.post<AddtoAlbumResponse>(url, imageIds);
  }

  // get /api/v1/getAlbumByAlbumId/{albumId}
  getAlbumByAlbumId(
    albumId: string | number | null,
    page: number,
    limit: number
  ): Observable<any> {
    const url = `${this.baseUrl}/getAlbumByAlbumId/${albumId}?page=${page}&limit=${limit}`;
    return this.http.get(url);
  }

  // delete /api/v1/deleteAlbumName/{albumId} delete /api/v1/deleteAlbum/{albumId}
  deleteAlbumName(deletedAlbumId: string | number | null): Observable<any> {
    const url = `${this.baseUrl}/deleteAlbum/${deletedAlbumId}`;
    return this.http.delete(url);
  }

  // put /api/v1/updateAlbumName/{albumId}
  updateAlbumById(
    payload:any,
  ): Observable<any> {
    let params = new HttpParams();


    const url = `${this.baseUrl}/updateAlbumName`;
    return this.http.put(url, payload);
  }

  // delete /api/v1/removeImages/{albumId} delete /api/v1/removeImages/{albumId}
  removeImageFromAlbum(
    albumId: string | number | null,
    artId: number | string
  ): Observable<any> {
    const artDetailIds = [artId]; // Create an array with the art ID
    const url = `${this.baseUrl}/removeImages/${albumId}`; // Construct the URL

    const httpOptions = {
      body: artDetailIds // Pass the array as the request body
    };

    return this.http.delete(url, httpOptions); // Make the DELETE request
  }

  getArtBySearchOnExplore(searchTerm:string,limit?: number, page: number = 0): Observable<GetArtBySearchOnExploreResponse>{
    let params = new HttpParams()
    if(searchTerm) {
      params = params.set("search", searchTerm)
    }
    if(limit) {
      params = params.set("limit", limit);
      params = params.set("page", page);
    }
    const userId = this.storageService.getUserId();
    if(userId) {
      params = params.set("userId", userId);
    }
    return this.http.get<GetArtBySearchOnExploreResponse>(`${this.baseUrl}/getArtBySearchOnExplore?`, { params });
  }

  getArtBySearchOnFollowArtist(searchTerm:string,limit?: number, page?: number){
    let url = `${this.baseUrl}/getArtBySearchOnFollowArtist?limit=${limit}&page=${page}&search=${searchTerm}`;
    return this.http.get(url);
  }

  getArtBySearchOnMyLikes(searchTerm:string,limit?: number, page?: number){
    let url = `${this.baseUrl}/getArtsBySearchOnMyLikes?limit=${limit}&page=${page}&search=${searchTerm}`;
    return this.http.get(url);
  }

  getArtBySearchOnMySubscriptions(searchTerm:string,limit?: number, page?: number){
    let url = `${this.baseUrl}/getArtsBySearchOnMySubscriptions?limit=${limit}&page=${page}&search=${searchTerm}`;
    return this.http.get(url);
  }

  getArtistSubscriptionPlans(artistId:string | number,limit?: number, page?: number){
    const userDetails = JSON.parse(this.storageService.getUserData(LOCAL_STORAGE.USER_DETAILS));
    const audienceId = userDetails.audienceProfileId;
    let params = new HttpParams();
    params = params.set("artistId", artistId);
    if(limit !== undefined)
    params = params.set("limit", limit);
    if(page !== undefined)
    params = params.set("page", page);
    if(audienceId) {
      params = params.set("audienceId", audienceId);
    }
    let url = `${this.baseUrl}/getArtistSubscriptionPlans`;
    return this.http.get(url, { params });
  }

  // post /api/v1/createCheckOutSession
  createCheckOutSession(artistId:number,subscriptionPlanId:number, paymentMethodId: string | null = null): Observable<any> {

    this.redirectService.saveRoute('onSubscribe', this.router.url);
    let params = new HttpParams()
      .set('subscriptionPlanId', subscriptionPlanId)
      .set('artistId', artistId);
    if (paymentMethodId) {
      params = params.set('paymentMethodId', paymentMethodId);
    }
    let url = `${this.baseUrl}/createCheckOutSession`

    return this.http.post(url,null, { params });
  }

  validateAlbumName(name:string): Observable<any> {
    return this.http.get( `${this.baseUrl}/validateAlbumName?albumName=${name}`);
  }

  deleteAlbumPlan(albumId:number, duration:string):Observable<any> {
    const params = new HttpParams().set("duration", duration);

    return this.http.delete( `${this.baseUrl}/updateAlbumDuration/${albumId}`, { params });
  }
}
